import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { graphql, useStaticQuery } from 'gatsby';
import { createOptionMap } from '../../../utilities/misc';

import { headers, body, richTextStyles } from '../../../styles/type';
import { colors } from '../../../theme/colors';
import { rem } from '../../../utilities/style';
import { media } from '../../../theme/media';
import badgeAmazonImage from '../../../images/store-badge-amazon.svg';
import badgeAppleImage from '../../../images/store-badge-apple.svg';
import badgeGoogleImage from '../../../images/store-badge-google.svg';
import badgeMicrosoftImage from '../../../images/store-badge-microsoft.svg';
import useUserContext from '../../../hooks/use-user-context';

import Link from '../../Link';
import Button from '../../buttons/Button';
import ButtonDropdown from '../../buttons/ButtonDropdown';
import ButtonGroup from '../../ButtonGroup';
import ButtonText from '../../buttons/ButtonText';
import TextRenderer from '../../TextRenderer';

const Container = styled.div`
  padding: ${rem(16)};
  border: 1px solid ${colors.gray[200]};
  border-radius: ${rem(10)};
  background-color: ${colors.gray[50]};

  ${media('>tiny')} {
    padding: ${rem(24)};
  }

  & + & {
    margin-top: ${rem(24)};
  }
`;

const Headline = styled.h2`
  ${headers.h5}
  margin-bottom: ${rem(16)};
  color: ${colors.blue['800']};
`;

const TextContent = styled.div`
  margin-bottom: ${rem(24)};
  font-size: ${rem(14)};
  ${richTextStyles}

  ${media('>tiny')} {
    font-size: ${rem(16)};
  }
`;

const MobileStores = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: ${rem(-6)};
  padding: ${rem(24)} 0;
`;

const MobileStoreLink = styled(Link)`
  margin: ${rem(6)};
`;

const BadgeImage = styled.img`
`;

const DisclaimerText = styled.div`
  ${body.small}
  ${richTextStyles}
`;

const DownloadTab = ({
  os,
  product,
  text,
  disclaimer,
  primaryButtonText,
  primaryButtonLink,
  secondaryButtonText,
  secondaryButtonLink,
  dropdownButton,
  appleStoreLink,
  googleStoreLink,
  amazonStoreLink,
  microsoftStoreLink,
  productName,
  currentTab
}) => {
  // debug.log(`currentTab: ${currentTab}`);
  // debug.log(`productName: ${productName}`);
  const { country, language } = useUserContext();
  // Getting global options
  const data = useStaticQuery(graphql`
    query HubspotForm {
      allContentfulOptionSet(filter: {name: {eq: "globals"}}) {
        nodes {
          ...OptionSetFragment
        }
      }
    }
  `);
  const options = createOptionMap(data.allContentfulOptionSet.nodes, language);

  // Conditionally set the googleStoreLink and primaryButtonLink based on the country
  const [modifiedGoogleStoreLink, setModifiedGoogleStoreLink] = useState(googleStoreLink);
  const [modifiedPrimaryButtonText, setModifiedPrimaryButtonText] = useState(primaryButtonText);
  const [modifiedPrimaryButtonLink, setModifiedPrimaryButtonLink] = useState(primaryButtonLink);

  useEffect(() => {
    let newPrimaryButtonText = primaryButtonText;
    let newPrimaryButtonLink = primaryButtonLink;
    let newGoogleStoreLink = googleStoreLink;

    // Logging the initial values
    // debug.log('🎃', { country, productName, os, product, currentTab });
    // For China Region
    if (country === 'cn') {
      newGoogleStoreLink = ''; // Clear the Google store link for China
      // Download Splashtop Business App link
      if (productName === 'enterprise' || productName === 'sos' || productName === 'srs' || productName === 'sba') {
        if (os === '{{{mobile}}}' || os === '{{{chromebook}}}') {
          if (currentTab === 'remoteFrom') {
            newPrimaryButtonText = options['globals.download_for_android'];
            newPrimaryButtonLink = 'https://download-cn.splashtop.com/droidclient/stb/splashtop_business_arm.apk';
          }
          if (currentTab === 'remoteTo') {
            // Splashtop Streamer link
            if (product === 'Splashtop Streamer') {
              newPrimaryButtonText = options['globals.download_login_android'];
              newPrimaryButtonLink = 'https://redirect.splashtop.com/my/team_deployment';
            }
            // SOS App link
            if (product === 'Splashtop SOS') {
              newPrimaryButtonText = options['globals.download_for_android'];
              newPrimaryButtonLink = 'https://download-cn.splashtop.com/sos/SplashtopSOS.apk';
            }
          }
        }
        if (productName === 'enterprise' && os === '{{{mobile}}}') {
          // Splashtop AR link
          if (currentTab === 'remoteTo' && product === 'Splashtop AR') {
            newPrimaryButtonText = options['globals.download_for_android'];
            newPrimaryButtonLink = 'https://download-cn.splashtop.com/sosar/splashtop_sos_ar_arm.apk';
          }
        }
      }
      if (productName === 'on-prem' && os === '{{{mobile}}}') {
        // Splashtop On-Prem App link
        if (currentTab === 'remoteFrom') {
          newPrimaryButtonText = options['globals.download_for_android'];
          newPrimaryButtonLink = 'https://support-splashtoponprem.splashtop.com/hc/sections/900000016186';
        }
        if (currentTab === 'remoteTo' && product === 'Splashtop Streamer') {
          // Splashtop On-Prem Streamer link
          newPrimaryButtonText = options['globals.download_for_android'];
          newPrimaryButtonLink = 'https://support-splashtoponprem.splashtop.com/hc/sections/900000013543';
        }
      }
      if (productName === 'personal' && os === '{{{mobile}}}') {
        // Splashtop Personal App
        if (currentTab === 'remoteFrom') {
          newPrimaryButtonText = options['globals.download_for_android'];
          newPrimaryButtonLink = 'https://download.splashtop.com/droidclient/stp/splashtop_personal_arm.apk';
        }
      }
      if (productName === 'classroom' && (os === '{{{mobile}}}' || os === '{{{chromebook}}}')) {
        // Splashtop Classroom App
        if (currentTab === 'remoteFrom') {
          newPrimaryButtonText = options['globals.download_for_android'];
          newPrimaryButtonLink = 'https://download.splashtop.com/droidclient/classroom/splashtop_classroom_arm.apk';
        }
      }
    }
    // Set the updated state values
    setModifiedGoogleStoreLink(newGoogleStoreLink);
    setModifiedPrimaryButtonText(newPrimaryButtonText);
    setModifiedPrimaryButtonLink(newPrimaryButtonLink);
  }, [
    country,
    productName,
    os,
    product,
    currentTab,
    primaryButtonText,
    primaryButtonLink,
    googleStoreLink,
    options
  ]);
  /* eslint-disable react/no-array-index-key */
  return (
    <Container>
      {product && (
        <Headline>
          <TextRenderer text={product} />
        </Headline>
      )}
      {text && (
        <TextContent>
          {Array.isArray(text) ? text.map((paragraph, index) => (
            <p key={index}>
              <TextRenderer text={paragraph} />
            </p>
          )) : (
            <TextRenderer text={text} />
          )}
        </TextContent>
      )}
      <ButtonGroup>
        {dropdownButton && (
          <ButtonDropdown
            buttonText={dropdownButton.buttonText}
            buttonLink={dropdownButton.buttonLink}
            dropdownText={dropdownButton.dropdownText}
            dropdownItems={dropdownButton.dropdownItems}
          />
        )}
        {(modifiedPrimaryButtonText && modifiedPrimaryButtonLink) && (
          <Button to={modifiedPrimaryButtonLink}>
            <TextRenderer text={modifiedPrimaryButtonText} />
          </Button>
        )}
        {secondaryButtonText && secondaryButtonLink && (
          <ButtonText to={secondaryButtonLink}>
            <TextRenderer text={secondaryButtonText} />
          </ButtonText>
        )}
      </ButtonGroup>
      <MobileStores>
        {appleStoreLink && (
          <MobileStoreLink to={appleStoreLink}>
            <BadgeImage
              src={badgeAppleImage}
              width="120"
              height="40"
              alt="Download on the App Store"
              loading="lazy"
            />
          </MobileStoreLink>
        )}
        {modifiedGoogleStoreLink && (
          <MobileStoreLink to={modifiedGoogleStoreLink}>
            <BadgeImage
              src={badgeGoogleImage}
              width="136"
              height="40"
              alt="Download on Google Play"
              loading="lazy"
            />
          </MobileStoreLink>
        )}
        {amazonStoreLink && (
          <MobileStoreLink to={amazonStoreLink}>
            <BadgeImage
              src={badgeAmazonImage}
              width="138"
              height="40"
              alt="Download on Amazon"
              loading="lazy"
            />
          </MobileStoreLink>
        )}
        {microsoftStoreLink && (
          <MobileStoreLink to={microsoftStoreLink}>
            <BadgeImage
              src={badgeMicrosoftImage}
              width="112"
              height="40"
              alt="Download on Microsoft Store"
              loading="lazy"
            />
          </MobileStoreLink>
        )}
      </MobileStores>
      {disclaimer && (
        <DisclaimerText>
          {Array.isArray(disclaimer) ? disclaimer.map((paragraph, index) => (
            <p key={index}>
              <TextRenderer text={paragraph} />
            </p>
          )) : (
            <TextRenderer text={disclaimer} />
          )}
        </DisclaimerText>
      )}
    </Container>
  );
};

export default DownloadTab;
